import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Pause, Volume2 } from "lucide-react";
import { S, SANS, SERIF, IMG } from "./slUi";

const API = process.env.REACT_APP_BACKEND_URL;

// Adelaida voices a set of lines; each line lights up as she reaches it.
export const useSpeak = (key) => {
  const [state, setState] = useState("idle");
  const [shown, setShown] = useState(0);
  const audio = useRef(null);
  useEffect(() => () => audio.current?.pause(), []);
  const play = async () => {
    if (state === "playing") { audio.current.pause(); setState("paused"); return; }
    if (!audio.current) {
      setState("loading");
      try {
        const { data } = await axios.post(`${API}/api/soulumo-launch/say/${key}`);
        const total = data.lines.join(" ").length;
        const a = new Audio(`${API}${data.audio_url}`);
        a.ontimeupdate = () => {
          const at = (a.currentTime / (a.duration || 1)) * total;
          let acc = 0, n = 0;
          data.lines.forEach((l) => { if (at >= acc - 2) n += 1; acc += l.length + 1; });
          setShown(n);
        };
        a.onended = () => { setShown(data.lines.length); setState("done"); };
        audio.current = a;
      } catch { setState("error"); return; }
    }
    audio.current.play().then(() => setState("playing")).catch(() => setState("error"));
  };
  return { state, shown, play };
};

export const Portrait = ({ size = 64, speaking }) => (
  <span className="relative shrink-0 inline-block rounded-full" style={{ width: size, height: size }}>
    {speaking && <motion.span className="absolute -inset-1.5 rounded-full" style={{ border: `1px solid ${S.gold}` }} animate={{ opacity: [0.2, 0.9, 0.2], scale: [1, 1.06, 1] }} transition={{ duration: 1.6, repeat: Infinity }} />}
    <img src={IMG.adelaida} alt="Adelaida" className="w-full h-full rounded-full object-cover object-top" style={{ border: `1px solid ${S.goldSoft}` }} />
  </span>
);

export const SpeakButton = ({ s, label, testId, dark }) => (
  <button type="button" onClick={s.play} data-testid={testId}
    className="min-h-[48px] rounded-full pl-2 pr-5 py-2 inline-flex items-center gap-3 transition-[background-color] duration-200 hover:bg-white/10"
    style={{ border: `1px solid ${dark ? S.line : S.goldSoft}`, color: dark ? S.ink : S.cream, fontFamily: SANS, fontSize: "0.66rem", letterSpacing: "0.22em" }}>
    <span className="w-9 h-9 rounded-full inline-flex items-center justify-center" style={{ background: S.gold, color: S.night }}>
      {s.state === "loading" ? <Loader2 size={15} className="animate-spin" /> : s.state === "playing" ? <Pause size={15} /> : <Volume2 size={15} />}
    </span>
    <span className="uppercase">{s.state === "error" ? "Try again" : s.state === "done" ? "Hear again" : label}</span>
  </button>
);

// Lines that fade up in step with Adelaida's voice (all visible when she isn't speaking).
export const SpokenLines = ({ lines, s, big, light }) => (
  <div className="space-y-3">
    {lines.map((l, i) => {
      const live = s.state === "playing" || s.state === "paused";
      return (
        <p key={l} className={`${i === 0 && big ? "text-4xl sm:text-5xl lg:text-6xl" : "text-xl sm:text-3xl"} font-light leading-tight transition-opacity duration-700`}
          style={{ fontFamily: SERIF, color: light ? S.ink : S.cream, opacity: live ? (i < s.shown ? 1 : 0.2) : 1 }}>{l}</p>
      );
    })}
  </div>
);
